//Built-in libraries

import { TrashIcon } from '@heroicons/react/24/outline';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
//Third party libraries
import * as Yup from 'yup';
import type { ReactSelectOption } from '../../../../types/Constants';
//Services
import { formatNumber, projectBudgetTotal } from '../../../../utils';
//Custom components
import Button from '../../../UI/Button';
import HookMaskedInput from '../../../UI/HookForm/Form/MaskedInput';
import { HookReactSingleSelect } from '../../../UI/HookForm/Form/ReactSelect';
import { CurrencyFormatDisplay } from '../../../UI/NumberFormatDisplay/CurrencyFormatDisplay';

export const projectSubsidiesValidationSchema = Yup.object({
  subsidies: Yup.array().of(
    Yup.object({
      name: Yup.object({ label: Yup.string(), value: Yup.string() }).test(
        'subsidies',
        'You must select a valid subsidies',
        (val) => val.value !== undefined && val.label !== undefined
      ),
      amount: Yup.number().required('This is required').min(0, 'Amount must be 0 or greater'),
    })
  ),
});

const projectSubsidiesOptions: ReactSelectOption[] = [
  { label: 'Acquisition write-down', value: 'Acquisition write-down' },
  {
    label: 'Workforce development demo/clearing',
    value: 'Workforce development demo/clearing',
  },
  { label: 'Draw/Lender fee credit', value: 'Draw/Lender fee credit' },
  { label: 'Other', value: 'Other' },
];

const ProjectSubsidiesHookFieldArray = () => {
  const { trigger, getValues } = useFormContext();
  const { fields, append, remove } = useFieldArray({ name: 'subsidies' });
  const projectSubsidiesFormValues = useWatch({ name: 'subsidies' });
  const budgetTotal = formatNumber(projectBudgetTotal(projectSubsidiesFormValues));
  const addNewSubsidy = async () => {
    const projectSubsidiesValidation = await trigger('subsidies');
    if (projectSubsidiesValidation || getValues('subsidies').length === 0) {
      append({ name: { label: undefined, value: undefined }, amount: 0 });
    }
  };
  return (
    <>
      {fields.length <= 0 && <p className='text-sm text-tertiaryColor-normal'>There are no subsidies added into the project.</p>}
      {fields.map((subsidies, idx) => (
        <div key={subsidies.id} className='flex items-center justify-between gap-4 my-4'>
          <div className='grid grid-cols-2 gap-5 w-full'>
            <HookReactSingleSelect
              id='name'
              componentClassName='col-span-1'
              options={projectSubsidiesOptions}
              placeholder='Select subsidies'
              name={`subsidies.${idx}.name` as const}
              label='Line Item'
            />
            <HookMaskedInput
              customClassName='col-span-1'
              label='Amount ($)'
              name={`subsidies.${idx}.amount` as const}
              id='amount'
              maskedComponent='currency'
            />
          </div>
          <div
            className='h-full border p-2 mt-6 rounded-md text-neutralColor-normal bg-gray-50 hover:bg-tertiaryColor-lightest hover:text-tertiaryColor-normal cursor-pointer transition duration-100 ease-in-out'
            onClick={() => remove(idx)}
          >
            <TrashIcon className='h-5 w-6' />
          </div>
        </div>
      ))}
      <p className='mt-5 font-bold text-primaryColor-normal'>
        Total: <CurrencyFormatDisplay value={budgetTotal} prefix='$ ' />
      </p>
      <div className='mt-8'>
        <Button label='Add Subsidy' btnStyle='btn-primary' btnSize='btn-sm' type='button' onClick={addNewSubsidy} />
      </div>
    </>
  );
};

export default ProjectSubsidiesHookFieldArray;
